import { createFileRoute } from "@tanstack/react-router"
import { useCallback, useMemo, useState } from "react"
import { usePlatformBilling } from "../../hooks/usePlatformBilling"
import { requireAdmin } from "../../lib/route-guards"
import { PlatformBillingView } from "./-platform-billing/PlatformBillingView"
import { getDateRange, type TDateRange } from "./-usage/date-range"

/**
 * Super-admin platform billing / revenue surface. Real money (COGS, revenue,
 * profit) is intentional here — this is the OWNER view, gated by `requireAdmin`
 * in `beforeLoad` and independently enforced by the backend on `/api/admin/usage`.
 * It is deliberately absent from tenant navigation (only rendered for super-admins
 * in `_auth.tsx`).
 *
 * The smart wrapper owns the date-range preset (which windows the money figures)
 * and the query; the dumb `PlatformBillingView` renders the totals band + table.
 */
export const Route = createFileRoute("/_auth/platform-billing")({
  beforeLoad: requireAdmin,
  component: PlatformBillingPage,
})

function PlatformBillingPage(): React.JSX.Element {
  const [range, setRange] = useState<TDateRange>("mtd")
  const { from, to } = useMemo(() => getDateRange(range), [range])

  const { data, isLoading, isError } = usePlatformBilling({ from, to })

  const handleRangeChange = useCallback((next: TDateRange): void => {
    setRange(next)
  }, [])

  return (
    <PlatformBillingView
      overview={data ?? null}
      isLoading={isLoading}
      isError={isError}
      range={range}
      onRangeChange={handleRangeChange}
    />
  )
}
